"use client";

import { useEffect, useMemo, useRef, useState, type DragEvent } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { RiDeleteBinLine } from "react-icons/ri";
import { MdOutlineContentCopy, MdOutlineRemoveRedEye } from "react-icons/md";
import { IoClose, IoCheckmark } from "react-icons/io5";
import mainAPI from "../../services/main/mainAPI";
import sequenceAPI from "../../services/sequence/sequenceAPI";
import ConfirmPopUp from "../popup/confirmPopUp";
import CopyPopUp from "../popup/copyPopUp";
import StatusPopUp from "../popup/statusPopUp";
import { GamePreviewModal } from "../games/shared/GamePreviewModal";
import { formatDateShort } from "../../utils/formatDatetime";
import { useTranslations } from "next-intl";
import { useReportGameEditorLeaveGuard } from "../games/shared/GameEditorLeaveGuard";

type SequenceProps = {
    createdGameData?: {
        id: number | string;
        exercise_name?: string;
        uuid?: string;
    } | null;
};

type SequenceGameItem = {
    id: number | string;
    uuid: string;
    name: string;
    thumbnail: string;
    subject: string;
    level: string;
    groupName: string;
    gameTypeKey: string;
    gameTypeLabel: string;
    createdAt: string;
};

function buildThumbnailUrl(fileName: unknown) {
    const name = String(fileName ?? "").trim();
    if (!name) return "";
    if (name.startsWith("http://") || name.startsWith("https://")) return name;
    const baseUrl = process.env.NEXT_PUBLIC_BACKEND_URL || "https://thaipilacreate.eef.or.th";
    return `${baseUrl.replace(/\/$/, "")}/api/${encodeURIComponent(name)}`;
}

function getTeacherUuidFromLocalStorage() {
    try {
        const raw = localStorage.getItem("user");
        if (!raw) return "";
        const parsed = JSON.parse(raw) as { id?: string };
        return typeof parsed?.id === "string" ? parsed.id.trim() : "";
    } catch {
        return "";
    }
}

const GAME_TYPE_LABEL: Record<string, string> = {
    find_the_match: "Find the match",
    game_show_quiz: "Game show Quiz",
    flip_cards: "Flip cards",
    "air_plan/flying_fruits": "Flying Fruits",
    flappy_bird: "Flappy Chicken",
    whack_a_mole: "Whack a mole",
    anagram: "Anagram",
    complete_the_sentence: "Complete the Sentence",
    situation: "Situation",
};

const PREVIEW_BASE = "https://thaipilagame.eef.or.th";

function mapRow(item: unknown, i: number): SequenceGameItem {
    const row = item as Record<string, unknown>;
    const nestedGame = (
        (row.game as Record<string, unknown> | undefined) ??
        (row.game_info as Record<string, unknown> | undefined) ??
        {}
    );
    const pick = (...keys: string[]) => {
        for (const key of keys) {
            const nestedValue = nestedGame[key];
            if (nestedValue !== undefined && nestedValue !== null && String(nestedValue).trim() !== "") return nestedValue;
            const rootValue = row[key];
            if (rootValue !== undefined && rootValue !== null && String(rootValue).trim() !== "") return rootValue;
        }
        return "";
    };

    const pickNestedPath = (...paths: string[][]) => {
        for (const path of paths) {
            let current: unknown = row;
            let ok = true;
            for (const key of path) {
                if (!current || typeof current !== "object") {
                    ok = false;
                    break;
                }
                current = (current as Record<string, unknown>)[key];
            }
            if (!ok) {
                continue;
            }
            if (current !== undefined && current !== null && String(current).trim() !== "") {
                return current;
            }
        }
        return "";
    };

    const uuid = String(pick("uuid")).trim();
    const parsedId = Number(pick("id"));
    const gameTypeKey = String(pick("game_type")).trim();
    const rawDate = String(pick("create_at", "created_at")).trim();
    const resolvedGroupName = String(
        pick(
            "group_name",
            "groupName",
            "group",
            "group_topic",
            "groupTopic",
            "topic",
            "group_topic_name",
        ) || pickNestedPath(
            ["group", "group_name"],
            ["group", "name"],
            ["group_info", "group_name"],
            ["group_info", "name"],
            ["category_group", "group_name"],
            ["category_group", "name"],
        ),
    ).trim();

    return {
        id: Number.isFinite(parsedId) ? parsedId : uuid || `game_${i + 1}`,
        uuid,
        name: String(pick("exercise_name", "name") || "-").trim() || "-",
        thumbnail: buildThumbnailUrl(pick("thumbnail")),
        subject: String(pick("subject")).trim(),
        level: String(pick("level_class", "levelClass", "level")).trim(),
        groupName: resolvedGroupName,
        gameTypeKey,
        gameTypeLabel: GAME_TYPE_LABEL[gameTypeKey] ?? gameTypeKey,
        createdAt: rawDate ? formatDateShort(rawDate) : "-",
    };
}

// ── Row component (shared between both panels) ─────────────────────────────
type GameRowProps = {
    game: SequenceGameItem;
    side: "left" | "right";
    draggingId: string;
    onDragStart: (e: DragEvent<HTMLDivElement>, id: string) => void;
    onDragEnd: () => void;
    onDrop?: (e: DragEvent<HTMLDivElement>, targetId: string) => void;
    onDelete?: (id: string) => void;
    onPreview?: (game: SequenceGameItem) => void;
    onCopyUuid?: (uuid: string, name: string) => void;
    copiedId?: string | null;
};

function GameRow({ game, side, onDragStart, onDragEnd, onDrop, onDelete, onPreview, onCopyUuid, copiedId }: GameRowProps) {
    const rowGridClass =
        side === "left"
            ? "grid-cols-[1fr_28px] sm:grid-cols-[minmax(0,1.5fr)_minmax(150px,1fr)_110px_110px_90px_28px]"
            : "grid-cols-[1fr_52px] sm:grid-cols-[minmax(120px,3fr)_minmax(96px,1fr)_minmax(0,80px)_minmax(0,80px)_76px_28px_36px]";
    const rowGapClass = side === "left" ? "gap-x-4 sm:gap-x-6" : "gap-x-2 sm:gap-x-3";

    return (
        <div
            draggable={true}
            onDragStart={(e) => onDragStart(e, String(game.id))}
            onDragEnd={onDragEnd}
            onDragOver={(e) => e.preventDefault()}
            onDrop={onDrop ? (e) => { e.preventDefault(); e.stopPropagation(); onDrop(e, String(game.id)); } : undefined}
            className={`grid cursor-grab items-center rounded-lg bg-neutral-100 px-3 py-2.5 text-sm hover:bg-[#f0faff] ${rowGapClass} ${rowGridClass}`}
        >
            <span className="min-w-0 truncate font-medium text-neutral-800" title={game.name}>{game.name}</span>
            <span className="hidden min-w-0 truncate text-xs text-[##2B2B2B] sm:block">{game.gameTypeLabel || "-"}</span>
            <span className="hidden truncate text-xs text-[##2B2B2B] sm:block">{game.groupName || "-"}</span>
            <span className="hidden truncate text-xs text-[##2B2B2B] sm:block">{game.subject || "-"}</span>
            <span className="hidden text-xs text-[##2B2B2B] sm:block">{game.createdAt}</span>
            {side === "right" ? (
                <span>
                    <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); onPreview?.(game); }}
                        className="inline-flex h-7 w-7 cursor-pointer items-center justify-center rounded-md border border-[#0092D7] bg-[#DAF2FF] text-[#138FC5] hover:bg-[#c8ebff]"
                        title="Preview"
                        aria-label="Preview"
                    >
                        <MdOutlineRemoveRedEye className="text-base" />
                    </button>
                </span>
            ) : null}
            <span className="flex items-center justify-end gap-1.5">
                {side === "left" ? (
                    <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); onDelete?.(String(game.id)); }}
                        className="cursor-pointer text-[##2B2B2B] hover:text-rose-500"
                    >
                        <RiDeleteBinLine className="text-lg" />
                    </button>
                ) : (
                    <div className="relative">
                        <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); onCopyUuid?.(game.uuid, game.name); }}
                            className="cursor-pointer text-[##2B2B2B] hover:text-sky-600"
                            title="Copy UUID"
                        >
                            {copiedId === game.uuid ? (
                                <IoCheckmark className="text-sm text-emerald-600" />
                            ) : (
                                <MdOutlineContentCopy className="text-lg" />
                            )}
                        </button>
                    </div>
                )}
            </span>
        </div>
    );
}


// ── Main component ─────────────────────────────────────────────────────────
export default function Sequence({ createdGameData }: SequenceProps) {
    const tSQ = useTranslations("sequence");
    const [sequenceName, setSequenceName] = useState("");
    const initialSequenceNameRef = useRef("");
    const [selectedGames, setSelectedGames] = useState<SequenceGameItem[]>([]);
    const [allGames, setAllGames] = useState<SequenceGameItem[]>([]);
    const [savedGameIdKeys, setSavedGameIdKeys] = useState<string[]>([]);
    const [draggingId, setDraggingId] = useState("");
    const [isLoadingGames, setIsLoadingGames] = useState(false);
    const [isConfirmSaveOpen, setIsConfirmSaveOpen] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [statusPopUp, setStatusPopUp] = useState<{ title: string; message: string; type: "info" | "success" | "error" } | null>(null);
    const [previewGame, setPreviewGame] = useState<SequenceGameItem | null>(null);
    const [sequencePreviewOpen, setSequencePreviewOpen] = useState(false);
    const [addByCodeOpen, setAddByCodeOpen] = useState(false);
    const [addByCodeInput, setAddByCodeInput] = useState("");
    const [addByCodeResult, setAddByCodeResult] = useState<SequenceGameItem | null>(null);
    const [copiedId, setCopiedId] = useState<string | null>(null);
    const [copyTarget, setCopyTarget] = useState<{ uuid: string; name: string; detailLine: string } | null>(null);
    const [isCopying, setIsCopying] = useState(false);

    // filters
    const [filterSearch, setFilterSearch] = useState("");
    const [filterDateFrom, setFilterDateFrom] = useState<Date | null>(null);
    const [filterDateTo, setFilterDateTo] = useState<Date | null>(null);
    const [filterSubject, setFilterSubject] = useState(tSQ("category"));
    const [filterGroup, setFilterGroup] = useState(tSQ("group"));
    const [filterGameType, setFilterGameType] = useState(tSQ("game_type"));

    useEffect(() => {
        const initial = createdGameData?.exercise_name?.trim() || "";
        setSequenceName(initial);
        initialSequenceNameRef.current = initial;
    }, [createdGameData?.exercise_name]);

    useEffect(() => {
        let mounted = true;
        const load = async () => {
            setIsLoadingGames(true);
            const teacherUuid = getTeacherUuidFromLocalStorage();
            if (!teacherUuid) { setAllGames([]); setIsLoadingGames(false); return; }
            const response = await mainAPI.getAllLessonLibraryByTeacherUuid(teacherUuid);
            if (!mounted) return;
            if (!response || (response as { status?: string }).status === "failed") { setAllGames([]); setIsLoadingGames(false); return; }
            const rows = Array.isArray(response) ? response
                : Array.isArray((response as { data?: unknown[] }).data) ? (response as { data: unknown[] }).data : [];
            const filtered = rows.filter((item) => {
                const row = item as Record<string, unknown>;
                const source = String(row.source ?? "").toLowerCase();
                const deleted = row.delete_at ?? row.deleteAt ?? null;
                return source === "game" && (deleted === null || deleted === undefined || String(deleted).trim() === "");
            });
            setAllGames(filtered.map(mapRow));
            setIsLoadingGames(false);
        };
        void load();
        return () => { mounted = false; };
    }, []);

    useEffect(() => {
        let mounted = true;
        const uuid = String(createdGameData?.uuid ?? "").trim();
        if (!uuid) { setSelectedGames([]); setSavedGameIdKeys([]); return; }
        setSavedGameIdKeys([]);
        const loadSelected = async () => {
            const response = await sequenceAPI.getGameInSequenceByUuid(uuid);
            if (!mounted) return;
            if (!response || (response as { status?: string }).status === "failed") { setSelectedGames([]); setSavedGameIdKeys([]); return; }
            const games = Array.isArray((response as { games?: unknown[] }).games) ? (response as { games: unknown[] }).games : [];
            const mapped = games
                .filter((item) => { const g = item as Record<string, unknown>; const del = g.delete_at ?? g.deleteAt ?? null; return del === null || del === undefined || String(del).trim() === ""; })
                .map(mapRow);
            setSelectedGames(mapped);
            setSavedGameIdKeys(mapped.map((g) => String(g.id)));
        };
        void loadSelected();
        return () => { mounted = false; };
    }, [createdGameData?.uuid]);

    useEffect(() => {
        if (allGames.length === 0) {
            return;
        }

        setSelectedGames((previous) => {
            let changed = false;

            const next = previous.map((game) => {
                const lookup = allGames.find(
                    (item) =>
                        String(item.id) === String(game.id)
                        || (game.uuid && item.uuid && item.uuid === game.uuid),
                );

                if (!lookup) {
                    return game;
                }

                const merged = {
                    ...game,
                    groupName: game.groupName || lookup.groupName,
                    subject: game.subject || lookup.subject,
                    level: game.level || lookup.level,
                    gameTypeKey: game.gameTypeKey || lookup.gameTypeKey,
                    gameTypeLabel: game.gameTypeLabel || lookup.gameTypeLabel,
                    createdAt: game.createdAt === "-" ? lookup.createdAt : game.createdAt,
                };

                if (
                    merged.groupName !== game.groupName
                    || merged.subject !== game.subject
                    || merged.level !== game.level
                    || merged.gameTypeKey !== game.gameTypeKey
                    || merged.gameTypeLabel !== game.gameTypeLabel
                    || merged.createdAt !== game.createdAt
                ) {
                    changed = true;
                }

                return merged;
            });

            return changed ? next : previous;
        });
    }, [allGames, selectedGames]);

    const selectedIdSet = useMemo(() => new Set(selectedGames.map((g) => String(g.id))), [selectedGames]);
    const availableGames = useMemo(() => allGames.filter((g) => !selectedIdSet.has(String(g.id))), [allGames, selectedIdSet]);

    const filteredAvailableGames = useMemo(() => {
        const keyword = filterSearch.trim().toLowerCase();
        return availableGames.filter((g) => {
            if (
                keyword
                && !g.name.toLowerCase().includes(keyword)
                && !g.subject.toLowerCase().includes(keyword)
                && !g.groupName.toLowerCase().includes(keyword)
            ) {
                return false;
            }
            if (filterSubject !== tSQ("category") && g.subject !== filterSubject) return false;
            if (filterGroup !== tSQ("group") && g.groupName !== filterGroup) return false;
            if (filterGameType !== tSQ("game_type") && g.gameTypeKey !== filterGameType) return false;
            if (filterDateFrom || filterDateTo) {
                const parts = g.createdAt.split("/");
                if (parts.length === 3) {
                    const gameDate = new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
                    if (filterDateFrom) {
                        const from = new Date(filterDateFrom);
                        from.setHours(0, 0, 0, 0);
                        if (gameDate < from) return false;
                    }
                    if (filterDateTo) {
                        const to = new Date(filterDateTo);
                        to.setHours(23, 59, 59, 999);
                        if (gameDate > to) return false;
                    }
                }
            }
            return true;
        });
    }, [availableGames, filterSearch, filterDateFrom, filterDateTo, filterSubject, filterGroup, filterGameType]);

    const availableSubjects = useMemo(
        () => [...new Set(availableGames.map((g) => g.subject).filter(Boolean))],
        [availableGames],
    );
    const availableGroups = useMemo(
        () => [...new Set(availableGames.map((g) => g.groupName).filter(Boolean))],
        [availableGames],
    );
    const availableGameTypeOptions = useMemo(
        () => [...new Set(availableGames.map((g) => g.gameTypeKey).filter(Boolean))].map((key) => ({
            value: key,
            label: GAME_TYPE_LABEL[key] ?? key,
        })),
        [availableGames],
    );

    const pendingSaveGames = useMemo(() => { const saved = new Set(savedGameIdKeys); return selectedGames.filter((g) => !saved.has(String(g.id))); }, [selectedGames, savedGameIdKeys]);
    const pendingDeleteGameIdKeys = useMemo(() => selectedGames.filter((g) => !selectedIdSet.has(String(g.id))).map(() => "").filter(Boolean), [selectedGames, selectedIdSet]);
    const pendingDeleteIds = useMemo(() => savedGameIdKeys.filter((id) => !selectedIdSet.has(id)), [savedGameIdKeys, selectedIdSet]);

    const trimmedName = sequenceName.trim();
    const hasPendingNameUpdate = Boolean(createdGameData?.id) && trimmedName !== initialSequenceNameRef.current;
    const canSave = pendingSaveGames.length > 0 || pendingDeleteIds.length > 0 || hasPendingNameUpdate;

    const moveToSelected = (gameId: string) => {
        const target = allGames.find((g) => String(g.id) === gameId);
        if (!target || selectedIdSet.has(gameId)) return;
        setSelectedGames((prev) => [...prev, target]);
    };

    const reorder = (dragId: string, dropId: string) => {
        if (!dragId || !dropId || dragId === dropId) return;
        setSelectedGames((prev) => {
            const dragIdx = prev.findIndex((g) => String(g.id) === dragId);
            const dropIdx = prev.findIndex((g) => String(g.id) === dropId);
            if (dropIdx < 0) return prev;
            if (dragIdx < 0) {
                const target = allGames.find((g) => String(g.id) === dragId);
                if (!target || prev.some((g) => String(g.id) === dragId)) return prev;
                const next = [...prev]; next.splice(dropIdx, 0, target); return next;
            }
            const next = [...prev]; const [moved] = next.splice(dragIdx, 1); next.splice(dropIdx, 0, moved); return next;
        });
    };

    const handleConfirmSave = async () => {
        if (!canSave || !createdGameData?.id) { setIsConfirmSaveOpen(false); return; }
        setIsSaving(true);
        if (hasPendingNameUpdate) {
            if (!trimmedName) { setIsSaving(false); setIsConfirmSaveOpen(false); setStatusPopUp({ title: tSQ("failure"), message: tSQ("enter_sequence_name"), type: "error" }); return; }
            const res = await sequenceAPI.updateExcerciseName(createdGameData.id, trimmedName);
            if (!res || (res as { status?: string }).status === "failed") { setIsSaving(false); setIsConfirmSaveOpen(false); setStatusPopUp({ title: tSQ("failure"), message: tSQ("update_name_failure"), type: "error" }); return; }
            initialSequenceNameRef.current = trimmedName;
        }
        let nextSaved = [...savedGameIdKeys];
        if (pendingSaveGames.length > 0) {
            const res = await sequenceAPI.addGameToSequence(pendingSaveGames.map((g) => ({ sequenceId: createdGameData.id, gameId: g.id })));
            if (!res || (res as { status?: string }).status === "failed") { setIsSaving(false); setIsConfirmSaveOpen(false); setStatusPopUp({ title: tSQ("failure"), message: tSQ("save_sequence_failure"), type: "error" }); return; }
            nextSaved = Array.from(new Set([...nextSaved, ...pendingSaveGames.map((g) => String(g.id))]));
        }
        if (pendingDeleteIds.length > 0) {
            const res = await sequenceAPI.hardDeleteGameInSequence({ sequenceId: createdGameData.id, gameIds: pendingDeleteIds });
            if (!res || (res as { status?: string }).status === "failed") { setSavedGameIdKeys(nextSaved); setIsSaving(false); setIsConfirmSaveOpen(false); setStatusPopUp({ title: tSQ("failure"), message: tSQ("delete_game_failure"), type: "error" }); return; }
            const delSet = new Set(pendingDeleteIds); nextSaved = nextSaved.filter((id) => !delSet.has(id));
        }
        setSavedGameIdKeys(nextSaved); setIsSaving(false); setIsConfirmSaveOpen(false);
        setStatusPopUp({ title: tSQ("success"), message: tSQ("save_sequence_success"), type: "success" });
    };

    const previewUrl = previewGame ? `${PREVIEW_BASE}/${encodeURIComponent(previewGame.uuid)}` : null;
    const sequencePreviewUrl = createdGameData?.uuid ? `${PREVIEW_BASE}/${encodeURIComponent(createdGameData.uuid)}` : null;
    const isSaved = Boolean(createdGameData?.uuid) && !canSave;
    useReportGameEditorLeaveGuard(!isSaved);

    const handleCopyUuid = (uuid: string, gameName?: string) => {
        const foundGame = allGames.find((game) => game.uuid === uuid);
        const resolvedName = (gameName ?? foundGame?.name ?? uuid).trim();
        const groupName = foundGame?.groupName?.trim();
        const subject = foundGame?.subject?.trim();
        const detailParts = [groupName, subject].filter(Boolean);
        setCopyTarget({
            uuid,
            name: resolvedName || uuid,
            detailLine: detailParts.join(" | ") || "-",
        });
    };

    const handleConfirmCopyUuid = async () => {
        if (!copyTarget?.uuid || isCopying) {
            return;
        }
        setIsCopying(true);
        const copyValue = copyTarget.uuid;

        try {
            if (navigator.clipboard?.writeText) {
                await navigator.clipboard.writeText(copyValue);
            } else {
                const ta = document.createElement("textarea");
                ta.value = copyValue;
                ta.style.position = "fixed";
                ta.style.opacity = "0";
                document.body.appendChild(ta);
                ta.focus();
                ta.select();
                document.execCommand("copy");
                document.body.removeChild(ta);
            }
            setCopiedId(copyValue);
            setTimeout(() => setCopiedId(null), 1500);
        } catch {
            // keep popup open so the user can retry or close manually
        } finally {
            setIsCopying(false);
        }
    };

    return (
        <>
        <section className="grid h-full grid-cols-1 gap-4 xl:grid-cols-2">

            {/* ── LEFT PANEL ── */}
            <div className="flex min-h-[420px] flex-col rounded-2xl border border-neutral-200 bg-white p-3 shadow-sm sm:p-5">
                {/* name input */}
                <p className="mb-1.5 text-sm font-semibold text-neutral-600">{tSQ("sequence_name")}</p>
                <div className="flex flex-col gap-2 sm:flex-row">
                    <input
                        value={sequenceName}
                        onChange={(e) => setSequenceName(e.target.value)}
                        placeholder={tSQ("enter_sequence_name")}
                        className="flex-1 rounded-lg border border-neutral-300 bg-neutral-100 px-3 py-2 text-sm text-neutral-800 outline-none focus:border-[#22A8DF] focus:bg-white"
                    />
                    <button
                        type="button"
                        disabled={!isSaved}
                        onClick={() => { if (isSaved) setSequencePreviewOpen(true); }}
                        title={!isSaved ? tSQ("save_sequence_before_preview") : tSQ("preview")}
                        aria-label="Preview sequence"
                        className={`inline-flex h-10 w-full items-center justify-center rounded-lg border border-[#0092D7] transition-opacity sm:w-10 ${isSaved ? "cursor-pointer bg-[#DAF2FF] text-[#138FC5] hover:bg-[#c8ebff]" : "cursor-not-allowed bg-[#DAF2FF] text-[#9fbccc] opacity-50"}`}
                    >
                        <MdOutlineRemoveRedEye className="text-lg" />
                    </button>
                </div>

                {/* add by code + save */}
                <div className="mt-3 mb-4 flex items-center justify-between gap-3">
                    <button
                        type="button"
                        onClick={() => { setAddByCodeInput(""); setAddByCodeResult(null); setAddByCodeOpen(true); }}
                        className="cursor-pointer rounded-lg bg-[#0092D7] px-4 py-1.5 text-sm font-semibold text-white hover:bg-[#007fbc]"
                    >
                        {tSQ("add_by_code")}
                    </button>
                    <button
                        type="button"
                        onClick={() => { if (canSave && !isSaving) setIsConfirmSaveOpen(true); }}
                        disabled={!canSave || isSaving}
                        className={`rounded-lg px-10 py-1.5 text-sm font-semibold transition-colors ${!canSave || isSaving ? "cursor-not-allowed bg-neutral-300 text-[##2B2B2B]" : "cursor-pointer bg-[#FDEF5C] text-[#4E4E4E] hover:bg-[#eae356]"}`}
                    >
                        {isSaving ? tSQ("saving") : tSQ("save")}
                    </button>
                </div>

                {/* selected games */}
                <div
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => { e.preventDefault(); const id = e.dataTransfer.getData("text/plain") || draggingId; if (id) { moveToSelected(id); setDraggingId(""); } }}
                    className="flex-1 overflow-y-auto"
                >
                    {selectedGames.length === 0 ? (
                        <div className="flex h-20 items-center justify-center rounded-lg border-2 border-dashed border-neutral-200 text-sm text-[##2B2B2B]">
                            {tSQ("drag_game_or_add_by_code")}
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {selectedGames.map((game) => (
                                <GameRow
                                    key={String(game.id)}
                                    game={game}
                                    side="left"
                                    draggingId={draggingId}
                                    onDragStart={(e, id) => { e.dataTransfer.setData("text/plain", id); setDraggingId(id); }}
                                    onDragEnd={() => setDraggingId("")}
                                    onDrop={(e, targetId) => { reorder(e.dataTransfer.getData("text/plain") || draggingId, targetId); setDraggingId(""); }}
                                    onDelete={(id) => setSelectedGames((prev) => prev.filter((g) => String(g.id) !== id))}
                                />
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* ── RIGHT PANEL ── */}
            <div className="flex min-h-[420px] flex-col rounded-2xl border border-neutral-200 bg-white p-3 shadow-sm sm:p-5">
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                    <input
                        value={filterSearch}
                        onChange={(e) => setFilterSearch(e.target.value)}
                        placeholder={tSQ("search")}
                        className="rounded-lg border border-neutral-300 bg-neutral-50 px-3 py-2 text-sm outline-none focus:border-[#22A8DF] focus:bg-white"
                    />
                    <DatePicker
                        selectsRange
                        startDate={filterDateFrom ?? undefined}
                        endDate={filterDateTo ?? undefined}
                        onChange={([start, end]) => { setFilterDateFrom(start ?? null); setFilterDateTo(end ?? null); }}
                        isClearable
                        placeholderText={tSQ("time_range")}
                        dateFormat="dd/MM/yyyy"
                        className="w-full rounded-lg border border-neutral-300 bg-neutral-50 px-3 py-2 text-sm text-neutral-600 outline-none focus:border-[#22A8DF]"
                        wrapperClassName="w-full"
                    />
                </div>

                <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3 sm:gap-3">
                    <select
                        value={filterGameType}
                        onChange={(e) => setFilterGameType(e.target.value)}
                        className="h-10 rounded-xl border border-neutral-300 bg-white px-4 text-sm text-neutral-700 outline-none focus:border-[#0092D7]"
                    >
                        <option value="ประเภทเกม">{tSQ("game_type")}</option>
                        {availableGameTypeOptions.map((option) => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                    </select>
                    <select
                        value={filterSubject}
                        onChange={(e) => setFilterSubject(e.target.value)}
                        className="h-10 rounded-xl border border-neutral-300 bg-white px-4 text-sm text-neutral-700 outline-none focus:border-[#0092D7]"
                    >
                        <option value="หมวดหมู่ / วิชา">{tSQ("category")}</option>
                        {availableSubjects.map((subject) => (
                            <option key={subject} value={subject}>{subject}</option>
                        ))}
                    </select>
                    <select
                        value={filterGroup}
                        onChange={(e) => setFilterGroup(e.target.value)}
                        className="h-10 rounded-xl border border-neutral-300 bg-white px-4 text-sm text-neutral-700 outline-none focus:border-[#0092D7]"
                    >
                        <option value="กลุ่ม / หัวข้อ">{tSQ("group")}</option>
                        {availableGroups.map((group) => (
                            <option key={group} value={group}>{group}</option>
                        ))}
                    </select>
                </div>

                {/* game list */}
                <div className="mt-3 flex-1 overflow-y-auto">
                    {isLoadingGames ? (
                        <div className="flex h-20 items-center justify-center text-sm text-[##2B2B2B]">{tSQ("loading_data")}</div>
                    ) : filteredAvailableGames.length === 0 ? (
                        <div className="flex h-20 items-center justify-center text-sm text-[##2B2B2B]">{tSQ("no_data_found")}</div>
                    ) : (
                        <div className="space-y-2">
                            {filteredAvailableGames.map((game) => (
                                <GameRow
                                    key={String(game.id)}
                                    game={game}
                                    side="right"
                                    draggingId={draggingId}
                                    onDragStart={(e, id) => { e.dataTransfer.setData("text/plain", id); setDraggingId(id); }}
                                    onDragEnd={() => setDraggingId("")}
                                    onPreview={(g) => setPreviewGame(g)}
                                    onCopyUuid={handleCopyUuid}
                                    copiedId={copiedId}
                                />
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* ── ADD BY CODE POPUP ── */}
            {addByCodeOpen ? (
                <div className="fixed inset-0 z-70 flex items-center justify-center bg-black/40 p-4">
                    <div className="w-full max-w-[420px] rounded-2xl bg-white p-6 shadow-[0_8px_32px_rgba(0,0,0,0.18)]">
                        {/* header */}
                        <div className="flex items-start justify-between">
                            <div>
                                <h3 className="text-base font-bold text-neutral-800">{tSQ("add_exercise_by_code")}</h3>
                                <p className="mt-1 text-[13px] leading-5 text-[##2B2B2B]">
                                    {tSQ("enter_exercise_code_to_add")}<br />{tSQ("add_to_exercise")}
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={() => setAddByCodeOpen(false)}
                                className="ml-3 cursor-pointer text-[##2B2B2B] hover:text-neutral-600"
                            >
                                <IoClose className="text-xl" />
                            </button>
                        </div>

                        {/* input */}
                        <div className="mt-4">
                            <label className="mb-1.5 block text-sm font-semibold text-neutral-700">{tSQ("exercise_code")}</label>
                            <input
                                type="text"
                                value={addByCodeInput}
                                onChange={(e) => {
                                    const val = e.target.value.trim();
                                    setAddByCodeInput(e.target.value);
                                    const found = allGames.find((g) => g.uuid === val) ?? null;
                                    setAddByCodeResult(found);
                                }}
                                placeholder={tSQ("paste_uuid_here")}
                                className="w-full rounded-lg border border-neutral-300 px-3 py-2.5 text-sm outline-none focus:border-[#22A8DF]"
                            />
                        </div>

                        {/* result */}
                        <div className="mt-4">
                            <label className="mb-1.5 block text-sm font-semibold text-neutral-700">{tSQ("item_found")}</label>
                            <div className="min-h-[64px] rounded-lg border border-neutral-200 bg-neutral-50 px-3 py-2.5">
                                {addByCodeResult ? (
                                    <div>
                                        <p className="font-semibold text-neutral-800">{addByCodeResult.name}</p>
                                        <p className="mt-0.5 text-[13px] text-[##2B2B2B]">
                                            {[addByCodeResult.groupName, addByCodeResult.subject].filter(Boolean).join(" | ")}
                                            {addByCodeInput ? ` - ${addByCodeInput.trim().slice(0, 6)}...` : ""}
                                        </p>
                                    </div>
                                ) : addByCodeInput.trim() ? (
                                    <p className="text-sm text-[##2B2B2B]">{tSQ("no_matching_item")}</p>
                                ) : (
                                    <p className="text-sm text-neutral-300">—</p>
                                )}
                            </div>
                        </div>

                        {/* actions */}
                        <div className="mt-5 flex justify-end gap-3">
                            <button
                                type="button"
                                onClick={() => setAddByCodeOpen(false)}
                                className="cursor-pointer px-4 py-2 text-sm font-semibold text-[#22A8DF] hover:underline"
                            >
                                {tSQ("cancel")}
                            </button>
                            <button
                                type="button"
                                disabled={!addByCodeResult}
                                onClick={() => {
                                    if (!addByCodeResult) return;
                                    moveToSelected(String(addByCodeResult.id));
                                    setAddByCodeOpen(false);
                                }}
                                className={`rounded-lg px-5 py-2 text-sm font-semibold text-white transition-colors ${addByCodeResult ? "cursor-pointer bg-[#22A8DF] hover:bg-[#1f97c8]" : "cursor-not-allowed bg-neutral-300"}`}
                            >
                                {tSQ("add_item")}
                            </button>
                        </div>
                    </div>
                </div>
            ) : null}

            {/* ── SEQUENCE PREVIEW MODAL ── */}
            {sequencePreviewOpen && sequencePreviewUrl ? (
                <GamePreviewModal
                    src={sequencePreviewUrl}
                    iframeTitle="Sequence Preview"
                    onClose={() => setSequencePreviewOpen(false)}
                />
            ) : null}

            {/* ── PREVIEW MODAL ── */}
            {previewGame && previewUrl ? (
                <GamePreviewModal
                    src={previewUrl}
                    iframeTitle="Game Preview"
                    onClose={() => setPreviewGame(null)}
                    headerExtra={
                        <button
                            type="button"
                            onClick={() => {
                                moveToSelected(String(previewGame.id));
                                setPreviewGame(null);
                            }}
                            className="cursor-pointer rounded-lg bg-[#22A8DF] px-3 py-1.5 text-sm font-semibold text-white hover:bg-[#1f97c8]"
                        >
                            {tSQ("add_to_set")}
                        </button>
                    }
                />
            ) : null}

            {isConfirmSaveOpen ? (
                <ConfirmPopUp
                    title={tSQ("confirm_save")}
                    message={tSQ("confirm_save_sequence")}
                    cancelText={tSQ("cancel")}
                    confirmText={tSQ("confirm")}
                    onCancel={() => { if (!isSaving) setIsConfirmSaveOpen(false); }}
                    onConfirm={() => { void handleConfirmSave(); }}
                    isConfirming={isSaving}
                />
            ) : null}

            {statusPopUp ? (
                <StatusPopUp
                    title={statusPopUp.title}
                    message={statusPopUp.message}
                    type={statusPopUp.type}
                    onClose={() => setStatusPopUp(null)}
                />
            ) : null}

            <CopyPopUp
                isOpen={Boolean(copyTarget)}
                exerciseName={copyTarget?.name ?? ""}
                detailLine={copyTarget?.detailLine ?? "-"}
                copyId={copyTarget?.uuid ?? ""}
                isPreparing={false}
                isCopying={isCopying}
                onClose={() => { if (!isCopying) setCopyTarget(null); }}
                onConfirmCopy={() => {
                    void handleConfirmCopyUuid();
                }}
            />
        </section>
        </>
    );
}
